// rotateRingSector — aiming a 180° torus sector at the camera.
//
// The rotate gizmo draws only the camera-facing HALF of each axis ring
// (PlayCanvas `_shapesLookAtCamera`), so the far half can't be mistaken for a
// grabbable near one. Getting that half onto the right side is the whole job of
// this module.
//
// THE PROBLEM
// A TorusGeometry with `arc = π` spans its OWN local angles 0…π in the XY plane,
// hole along +Z. Two independent conventions then stand between that and a world
// direction:
//
//   1. BASE_EULER rotates the hole onto the rotation axis — but WHICH world
//      direction ends up at the geometry's angle 0 falls out of that rotation.
//   2. `planeBasis` (rotateArcAngle.js) picks its own reference vector, so its
//      angle 0 is somewhere else again.
//
// And there is a third, easier-to-miss term: BASE_EULER.y sends +Z to −Y, not
// +Y, so for the Y ring the geometry's angle DECREASES as planeBasis' angle
// increases. Aiming the sector without accounting for that flip puts the Y arc
// on the far side of the ring — the one half you can see is the half you are not
// reaching for.
//
// THE APPROACH
// Measure both terms once per axis instead of hand-deriving them: where angle 0
// lands (`phi0`) and which way angle increases (`sigma`, ±1). A table of magic
// angles would silently rot the moment either convention changed; two probe
// points cannot.
//
// Pure enough to unit-test directly, which is the point — the Y flip was found
// by derivation and is now pinned by test rather than by eye.

import * as THREE from 'three'
import { arcAngle } from './rotateArcAngle.js'

const ORIGIN = [0, 0, 0]

/**
 * Local→world rotation putting a torus (XY plane, hole along +Z) into the plane
 * perpendicular to each axis.
 */
export const BASE_EULER = Object.freeze({
  x: Object.freeze([0, Math.PI / 2, 0]),
  y: Object.freeze([Math.PI / 2, 0, 0]),
  z: Object.freeze([0, 0, 0]),
})

// Measured once per axis: { phi0, sigma }.
const PROBES = {}

/** Where geometry angle `theta` lands, as a planeBasis angle. */
function probe(axis, axisVec, theta) {
  const p = new THREE.Vector3(Math.cos(theta), Math.sin(theta), 0)
    .applyEuler(new THREE.Euler(...BASE_EULER[axis]))
  return arcAngle([p.x, p.y, p.z], ORIGIN, axisVec)
}

/**
 * `{ phi0, sigma }` for one axis: the planeBasis angle of the geometry's own
 * angle 0, and whether the two angles run the same way (+1) or opposite (−1).
 *
 * Exported for the test — the sign is the interesting half.
 */
export function sectorBasis(axis, axisVec) {
  if (PROBES[axis]) return PROBES[axis]
  const phi0 = probe(axis, axisVec, 0)
  // A small step, then the SHORTEST difference — a raw subtraction would read a
  // step across atan2's ±π seam as a huge move in the wrong direction.
  const eps = 0.05
  let d = probe(axis, axisVec, eps) - phi0
  if (d > Math.PI) d -= Math.PI * 2
  if (d <= -Math.PI) d += Math.PI * 2
  PROBES[axis] = { phi0, sigma: d >= 0 ? 1 : -1 }
  return PROBES[axis]
}

/**
 * The torus' local-Z spin that centres its 180° sector on `camAngle`.
 *
 * Derivation: with the measured basis, geometry angle θ appears at planeBasis
 * angle `phi0 + sigma·θ`. Applying a spin `s` (rotateZ, so θ → θ + s) puts the
 * sector θ∈[0,π] between `phi0 + sigma·s` and `phi0 + sigma·(s + π)`, whose
 * midpoint is `phi0 + sigma·s + sigma·π/2`. Setting that equal to `camAngle` and
 * solving — using 1/sigma === sigma — gives:
 *
 *     s = sigma·(camAngle − phi0) − π/2
 *
 * @param {'x'|'y'|'z'} axis
 * @param {number[]} axisVec  the ring's axis, same vector planeBasis was given
 * @param {number} camAngle   the camera's planeBasis angle in that ring's plane
 * @returns {number} radians to rotateZ the torus by
 */
export function sectorSpin(axis, axisVec, camAngle) {
  const { phi0, sigma } = sectorBasis(axis, axisVec)
  return sigma * (camAngle - phi0) - Math.PI / 2
}

/** Test seam: drop the measurement cache. */
export function _resetSectorCache() {
  for (const k of Object.keys(PROBES)) delete PROBES[k]
}
